import React, { useEffect, useRef, useState } from 'react';
import { Empty, Select, Tag } from 'antd';
import { Chart as ChartJS, LineController, LineElement, PointElement, LinearScale, Tooltip, Filler } from 'chart.js';
import { Chart } from './types';
import { formatDateTime } from '../../utils/format';

ChartJS.register(LineController, LineElement, PointElement, LinearScale, Tooltip, Filler);
const COLORS = ['#73bf69', '#f2cc0c', '#5794f2', '#ff9830', '#b877d9', '#f2495c', '#8ab8ff', '#fa6400', '#6ed0e0', '#efb843'];
const number = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 4 });

export function formatMetric(value: number, unit: Chart['unit']) {
  if (unit === 'percent') return `${number.format(value)}%`;
  if (unit === 'cores' || unit === 'count') return number.format(value);
  if (unit === 'seconds') {
    if (value >= 86400) return `${number.format(value / 86400)} d`;
    if (value >= 3600) return `${number.format(value / 3600)} h`;
    if (value >= 60) return `${number.format(value / 60)} min`;
    return `${number.format(value)} s`;
  }
  const suffixes = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const exponent = Math.min(suffixes.length - 1, Math.max(0, Math.floor(Math.log2(Math.abs(value) || 1) / 10)));
  return `${number.format(value / 1024 ** exponent)} ${suffixes[exponent]}${unit === 'bytes/sec' ? '/s' : ''}`;
}

export const MonitorChart: React.FC<{ chart: Chart; title: string }> = ({ chart, title }) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const instance = useRef<ChartJS<'line', { x: number; y: number | null }[]> | null>(null);
  const keyboardPoint = useRef(0);
  const [selected, setSelected] = useState<string[]>();
  const labels = chart.series.map((series) => series.label);
  const hasPoints = chart.series.some((series) => (selected === undefined || selected.includes(series.label)) && series.points.some((point) => point.value !== null));

  useEffect(() => {
    if (!canvas.current || !hasPoints) return;
    const visible = chart.series.filter((series) => selected === undefined || selected.includes(series.label));
    const from = Date.parse(chart.from);
    const to = Date.parse(chart.to);
    const peak = visible.reduce<number | undefined>((highest, series) => series.points.reduce((value, point) => {
      const time = Date.parse(point.time);
      if (point.value === null || !Number.isFinite(point.value) || time < from || time > to) return value;

      return value === undefined ? point.value : Math.max(value, point.value);
    }, highest), undefined);
    const plot = new ChartJS<'line', { x: number; y: number | null }[]>(canvas.current, {
      type: 'line',
      plugins: [{
        id: 'window-peak',
        afterDatasetsDraw: (plot) => {
          if (peak === undefined) return;

          const { ctx, chartArea, scales } = plot;
          const y = scales.y.getPixelForValue(peak);
          const label = `Max: ${formatMetric(peak, chart.unit)}`;
          ctx.save();
          ctx.strokeStyle = '#64748b';
          ctx.lineWidth = 1;
          ctx.setLineDash([6, 4]);
          ctx.beginPath();
          ctx.moveTo(chartArea.left, y);
          ctx.lineTo(chartArea.right, y);
          ctx.stroke();
          ctx.font = '12px sans-serif';
          ctx.textAlign = 'right';
          ctx.textBaseline = 'bottom';
          const labelY = Math.max(chartArea.top + 16, y - 4);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(chartArea.right - ctx.measureText(label).width - 8, labelY - 14, ctx.measureText(label).width + 8, 16);
          ctx.fillStyle = '#334155';
          ctx.fillText(label, chartArea.right - 4, labelY);
          ctx.restore();
        },
      }],
      data: { datasets: visible.map((series) => {
        const color = COLORS[chart.series.indexOf(series) % COLORS.length];
        const data = series.points.map((point) => ({ x: Date.parse(point.time), y: point.value }));
        const spacings = data.slice(1).map((point, index) => point.x - data[index].x).filter((spacing) => spacing > 0).sort((a, b) => a - b);
        return {
          label: series.label, data, borderColor: color, backgroundColor: `${color}18`,
          borderWidth: 1.5, pointRadius: data.length === 1 ? 2 : 0, pointHoverRadius: 4,
          fill: 'origin', tension: 0, spanGaps: chart.chart_id.startsWith('game-server-') ? false : (spacings[Math.floor(spacings.length / 2)] || Infinity) * 1.5,
        };
      }) },
      options: {
        responsive: true, maintainAspectRatio: false, animation: false, parsing: false,
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        scales: {
          x: {
            type: 'linear', min: Math.min(from, ...visible.map((series) => series.points.length ? Date.parse(series.points[0].time) : from)), max: to,
            grid: { color: '#e2e8f0' }, border: { display: false },
            ticks: { color: '#64748b', maxTicksLimit: 7, maxRotation: 0, callback: (value) => formatDateTime(Number(value)) },
          },
          y: {
            beginAtZero: true, suggestedMax: chart.chart_id === 'container-memory' ? 100 : undefined,
            grid: { color: '#e2e8f0' }, border: { display: false },
            ticks: { color: '#64748b', maxTicksLimit: 5, callback: (value) => formatMetric(Number(value), chart.unit) },
          },
        },
        plugins: { tooltip: {
          backgroundColor: '#ffffff', titleColor: '#334155', bodyColor: '#334155', borderColor: '#cbd5e1', borderWidth: 1,
          callbacks: {
            title: (items) => items.length ? formatDateTime(items[0].parsed.x!) : '',
            label: (item) => `${item.dataset.label}: ${formatMetric(item.parsed.y!, chart.unit)}`,
          },
        } },
      },
    });
    instance.current = plot;
    keyboardPoint.current = 0;
    return () => { plot.destroy(); instance.current = null; };
  }, [chart, selected, hasPoints]);

  const toggle = (label: string) => {
    const current = selected ?? labels;
    setSelected(current.includes(label) ? current.filter((item) => item !== label) : [...current, label]);
  };

  return <div className="monitor-chart">
    <div className="monitor-chart-controls">
      <span>{chart.interval} interval</span>
      <Select mode="multiple" showSearch aria-label={`${title} series`} placeholder="Select series" size="small"
        value={selected === undefined ? labels : selected.filter((label) => labels.includes(label))}
        options={labels.map((label) => ({ label, value: label }))} maxTagCount={0}
        maxTagPlaceholder={(values) => `${values.length} series`} style={{ width: 150 }} onChange={setSelected} />
    </div>
    {chart.unavailable_metrics?.map((metric) => <Tag color="orange" key={metric}>
      {metric === 'container-disk' ? 'Container disk metrics unavailable' : `${metric} unavailable`}
    </Tag>)}
    {!hasPoints ? <div className="monitor-chart-empty"><Empty description={chart.series.length ? 'No points in the selected series' : 'No metrics in this time range'} /></div>
      : <div className="monitor-plot"><canvas ref={canvas} role="img" tabIndex={0}
        aria-label={`${title}, ${chart.unit}. Use arrow keys to inspect values.`}
        onKeyDown={(event) => {
          const plot = instance.current;
          if (!plot) return;
          if (event.key === 'Escape') { plot.setActiveElements([]); plot.tooltip?.setActiveElements([], { x: 0, y: 0 }); plot.update(); return; }
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const count = Math.max(...plot.data.datasets.map((dataset) => dataset.data.length));
          keyboardPoint.current = Math.max(0, Math.min(count - 1, keyboardPoint.current + (event.key === 'ArrowLeft' ? -1 : 1)));
          const active = plot.data.datasets.flatMap((dataset, datasetIndex) => dataset.data[keyboardPoint.current] ? [{ datasetIndex, index: keyboardPoint.current }] : []);
          plot.setActiveElements(active);
          plot.tooltip?.setActiveElements(active, { x: plot.chartArea.left, y: plot.chartArea.top });
          plot.update();
        }}
        onBlur={() => { instance.current?.setActiveElements([]); instance.current?.tooltip?.setActiveElements([], { x: 0, y: 0 }); instance.current?.update(); }}>
        {title} time series. Values are summarized in the legend below.
      </canvas></div>}
    <div className="monitor-legend-scroll">
      <table className="monitor-legend" aria-label={`${title} legend`}>
        <thead><tr><th>Name</th><th>Last *</th><th>Max</th></tr></thead>
        <tbody>{chart.series.map((series, index) => {
          const last = series.points[series.points.length - 1];
          const max = series.points.reduce<number | undefined>((value, point) => point.value === null ? value : value === undefined ? point.value : Math.max(value, point.value), undefined);
          const enabled = selected === undefined || selected.includes(series.label);
          return <tr key={series.label} className={enabled ? undefined : 'monitor-series-hidden'}>
            <td><button type="button" aria-label={`${title}: ${series.label}`} aria-pressed={enabled} onClick={() => toggle(series.label)}>
              <span className="monitor-series-color" style={{ backgroundColor: COLORS[index % COLORS.length] }} />{series.label}
            </button></td>
            <td>{last?.value != null ? formatMetric(last.value, chart.unit) : '—'}</td><td>{max === undefined ? '—' : formatMetric(max, chart.unit)}</td>
          </tr>;
        })}</tbody>
      </table>
    </div>
  </div>;
};
